import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Card, CopyButton, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { TabHelp } from '../../../app/guides/TabHelp';
import { useT } from '../../../i18n/I18nProvider';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { BOOKINGS_KEY, bookingErrorText, useBookingSettings } from '../lib/data';
import '../bookings.css';

const MUTED = 'var(--rr-color-text-muted)';
const STEPS = [5, 10, 15, 20, 30, 45, 60];
const MAX_REMINDERS = 5;
const SLUG = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;
/** As the service allows (8 October 2026): up to eight letters, digits, hyphens, underscores or slashes, such as the default B- or T-. */
const PREFIX = /^[A-Za-z0-9/_-]{0,8}$/;

/** The tabs (8 October 2026), as on Ticket settings: one subject each, the choice kept in the address. */
const SECTIONS = ['times', 'reminders', 'confirmations', 'page', 'general'] as const;
type Section = (typeof SECTIONS)[number];
/** Where each problem is fixed, so saving with one jumps to its tab. */
const SECTION_OF: Record<string, Section> = {
  'bookings.settings.error.reminder': 'reminders',
  'bookings.settings.error.reminderDuplicate': 'reminders',
  'bookings.settings.error.slug': 'page',
  'bookings.settings.error.prefix': 'general'
};
/** The "show me" link each tab's question mark offers. */
const TAB_LINKS: Partial<Record<Section, { labelKey: string; to?: string; tour?: string }[]>> = {
  times: [{ labelKey: 'guide.bookings.link.howAppointments', tour: 'appointments' }],
  confirmations: [{ labelKey: 'guide.bookings.link.templates', to: '/templates' }],
  general: [{ labelKey: 'guide.bookings.link.billing', to: '/billing' }]
};

type Draft = Omit<bookings.SettingsUpdate, 'reminder_offsets_minutes' | 'public_slug' | 'business_address'> & {
  /** Hours before the start, as typed. */
  reminders: (number | '')[];
  public_slug: string;
  business_address: string;
};

function toDraft(s: bookings.Settings): Draft {
  return {
    slot_step_minutes: s.slot_step_minutes,
    min_notice_minutes: s.min_notice_minutes,
    horizon_days: s.horizon_days,
    change_cutoff_hours: s.change_cutoff_hours,
    reminders: [...s.reminder_offsets_minutes].sort((a, b) => b - a).map((m) => Math.round((m / 60) * 100) / 100),
    approval_by_default: s.approval_by_default,
    confirm_whatsapp: s.confirm_whatsapp,
    confirm_email: s.confirm_email,
    confirm_sms: s.confirm_sms,
    public_page_enabled: s.public_page_enabled,
    public_slug: s.public_slug ?? '',
    business_address: s.business_address ?? '',
    number_prefix: s.number_prefix
  };
}

function problemOf(d: Draft): string | null {
  if (d.reminders.some((r) => r === '' || r <= 0 || r > 24 * 14)) return 'bookings.settings.error.reminder';
  if (new Set(d.reminders).size !== d.reminders.length) return 'bookings.settings.error.reminderDuplicate';
  if (d.public_page_enabled && !SLUG.test(d.public_slug.trim())) return 'bookings.settings.error.slug';
  if (d.public_slug.trim() && !SLUG.test(d.public_slug.trim())) return 'bookings.settings.error.slug';
  if (!PREFIX.test(d.number_prefix.trim())) return 'bookings.settings.error.prefix';
  return null;
}

export default function BookingSettingsPage() {
  const t = useT();
  const qc = useQueryClient();
  const canEdit = useIsOrgAdmin();
  const q = useBookingSettings();
  const saved = q.data?.data;
  const [d, setD] = useState<Draft | null>(null);
  const [tried, setTried] = useState(false);
  const [params, setParams] = useSearchParams();
  const raw = params.get('section');
  const section: Section = (SECTIONS as readonly string[]).includes(raw ?? '') ? (raw as Section) : 'times';
  function choose(next: string) {
    const p = new URLSearchParams(params);
    p.set('section', next);
    setParams(p, { replace: true });
  }

  useEffect(() => { if (saved) setD(toDraft(saved)); }, [saved]);

  const set = (patch: Partial<Draft>) => setD((x) => (x ? { ...x, ...patch } : x));
  const dirty = !!saved && !!d && JSON.stringify(d) !== JSON.stringify(toDraft(saved));
  const problem = d ? problemOf(d) : null;

  const save = useMutation({
    mutationFn: () => bookings.saveSettings({
      slot_step_minutes: d!.slot_step_minutes,
      min_notice_minutes: d!.min_notice_minutes,
      horizon_days: d!.horizon_days,
      change_cutoff_hours: d!.change_cutoff_hours,
      reminder_offsets_minutes: d!.reminders.map((h) => Math.round(Number(h) * 60)),
      approval_by_default: d!.approval_by_default,
      confirm_whatsapp: d!.confirm_whatsapp,
      confirm_email: d!.confirm_email,
      confirm_sms: d!.confirm_sms,
      public_page_enabled: d!.public_page_enabled,
      public_slug: d!.public_slug.trim() || null,
      business_address: d!.business_address.trim() || null,
      number_prefix: d!.number_prefix.trim()
    }),
    onSuccess: (res) => {
      qc.setQueryData([BOOKINGS_KEY, 'settings'], res);
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      notifySuccess(t('bookings.settings.toast.saved'), t('bookings.settings.title'));
    },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.settings.title'))
  });

  function submit() {
    setTried(true);
    if (problem && SECTION_OF[problem] && SECTION_OF[problem] !== section) choose(SECTION_OF[problem]);
    if (!canEdit || problem) return;
    save.mutate();
  }

  const ro = !canEdit;
  const limits = saved?.limits;
  const err = (key: string) => (tried && problem === key ? t(key) : undefined);

  return (
    <Stack gap="lg">
      <PageHeader title={t('bookings.settings.title')} description={t('bookings.settings.description')} withDivider={false}
        actions={<HelpButton guide="bookings" />} />
      {ro && <Alert color="info">{t('bookings.readOnly')}</Alert>}
      {q.error && (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{bookingErrorText(t, q.error)}</span>
            <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}
      <Tabs value={section} onChange={choose}>
        <Tabs.List>
          {SECTIONS.map((s) => (
            <span key={s} className="rr-tabgroup">
              <Tabs.Tab value={s}>{t(`bookings.settings.section.${s}`)}</Tabs.Tab>
              <TabHelp guide="bookings" titleKey={`bookings.settings.section.${s}`} bodyKey={`guide.bookings.tab.${s}`} links={TAB_LINKS[s]} />
            </span>
          ))}
        </Tabs.List>
      </Tabs>

      {q.isPending && <Skeleton height={320} />}

      {d && saved && (
        <form noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {/* The page checks its own fields and says what is wrong under each one. The browser's own check stopped
              every save (8 October 2026): the default reminders of 24 and 2 hours failed its step test. */}
          <Stack gap="lg">
            {section === 'times' && (
            <Card withBorder data-guide="bookings-settings-times">
              <Stack gap="md">
                <Title order={4}>{t('bookings.settings.times')}</Title>
                <Group gap="sm" grow wrap="wrap" align="flex-start">
                  <Select
                    label={t('bookings.settings.slotStep')}
                    value={String(d.slot_step_minutes)}
                    onChange={(e) => set({ slot_step_minutes: Number(e.currentTarget.value) })}
                    data={(STEPS.includes(d.slot_step_minutes) ? STEPS : [...STEPS, d.slot_step_minutes].sort((a, b) => a - b))
                      .map((n) => ({ value: String(n), label: t('bookings.services.minutes', { n }) }))}
                    description={t('bookings.settings.slotStepHint')}
                    disabled={ro}
                  />
                  <NumberInput
                    label={t('bookings.settings.minNotice')}
                    value={Math.round((d.min_notice_minutes / 60) * 100) / 100}
                    onChange={(v) => set({ min_notice_minutes: v === '' ? 0 : Math.round(v * 60) })}
                    min={0}
                    max={24 * 30}
                    step={0.5}
                    description={t('bookings.settings.minNoticeHint')}
                    disabled={ro}
                  />
                </Group>
                <Group gap="sm" grow wrap="wrap" align="flex-start">
                  <NumberInput
                    label={t('bookings.settings.horizon')}
                    value={d.horizon_days}
                    onChange={(v) => set({ horizon_days: v === '' ? 1 : v })}
                    min={1}
                    max={365}
                    description={t('bookings.settings.horizonHint')}
                    disabled={ro}
                  />
                  <NumberInput
                    label={t('bookings.settings.cutoff')}
                    value={d.change_cutoff_hours}
                    onChange={(v) => set({ change_cutoff_hours: v === '' ? 0 : v })}
                    min={0}
                    max={24 * 14}
                    description={t('bookings.settings.cutoffHint')}
                    disabled={ro}
                  />
                </Group>
                <Switch
                  label={t('bookings.settings.approvalDefault')}
                  description={t('bookings.settings.approvalDefaultHint')}
                  checked={d.approval_by_default}
                  onChange={(on) => set({ approval_by_default: on })}
                  disabled={ro}
                />
              </Stack>
            </Card>

            )}

            {section === 'reminders' && (
            <Card withBorder data-guide="bookings-settings-reminders">
              <Stack gap="md">
                <Stack gap="xs">
                  <Title order={4}>{t('bookings.settings.reminders')}</Title>
                  <Text role="bodySmall" c={MUTED}>{t('bookings.settings.remindersIntro')}</Text>
                </Stack>
                {d.reminders.length === 0 && <Text role="bodySmall" c={MUTED}>{t('bookings.settings.noReminders')}</Text>}
                {d.reminders.map((h, i) => (
                  <Group key={i} gap="xs" align="flex-end" wrap="wrap">
                    <NumberInput
                      label={t('bookings.settings.hoursBefore')}
                      value={h}
                      onChange={(v) => set({ reminders: d.reminders.map((x, j) => (j === i ? v : x)) })}
                      min={0.25}
                      max={24 * 14}
                      step={0.25}
                      disabled={ro}
                    />
                    {canEdit && (
                      <Button size="xs" variant="subtle" color="red" aria-label={t('bookings.settings.removeReminder', { n: i + 1 })}
                        onClick={() => set({ reminders: d.reminders.filter((_, j) => j !== i) })}>
                        {t('action.remove')}
                      </Button>
                    )}
                  </Group>
                ))}
                {tried && (problem === 'bookings.settings.error.reminder' || problem === 'bookings.settings.error.reminderDuplicate') && (
                  <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>
                )}
                <Text role="caption" c={MUTED}>{t('bookings.settings.remindersHint')}</Text>
                {canEdit && d.reminders.length < MAX_REMINDERS && (
                  <div>
                    <Button size="xs" variant="subtle" onClick={() => set({ reminders: [...d.reminders, d.reminders.includes(24) ? 2 : 24] })}>
                      {t('bookings.settings.addReminder')}
                    </Button>
                  </div>
                )}
              </Stack>
            </Card>

            )}

            {section === 'confirmations' && (
            <Card withBorder data-guide="bookings-settings-confirmations">
              <Stack gap="md">
                <Stack gap="xs">
                  <Title order={4}>{t('bookings.settings.confirmations')}</Title>
                  <Text role="bodySmall" c={MUTED}>{t('bookings.settings.confirmationsIntro')}</Text>
                </Stack>
                <Switch label={t('bookings.settings.confirmWhatsapp')} description={t('bookings.settings.confirmWhatsappHint')} checked={d.confirm_whatsapp} onChange={(on) => set({ confirm_whatsapp: on })} disabled={ro} />
                <Switch label={t('bookings.settings.confirmEmail')} description={t('bookings.settings.confirmEmailHint')} checked={d.confirm_email} onChange={(on) => set({ confirm_email: on })} disabled={ro} />
                <Switch label={t('bookings.settings.confirmSms')} description={t('bookings.settings.confirmSmsHint')} checked={d.confirm_sms} onChange={(on) => set({ confirm_sms: on })} disabled={ro} />
              </Stack>
            </Card>

            )}

            {section === 'page' && (
            <Card withBorder data-guide="bookings-settings-public">
              <Stack gap="md">
                <Stack gap="xs">
                  <Title order={4}>{t('bookings.settings.publicPage')}</Title>
                  <Text role="bodySmall" c={MUTED}>{t('bookings.settings.publicPageIntro')}</Text>
                </Stack>
                <Switch label={t('bookings.settings.publicEnabled')} checked={d.public_page_enabled} onChange={(on) => set({ public_page_enabled: on })} disabled={ro} />
                <TextInput
                  label={t('bookings.settings.slug')}
                  value={d.public_slug}
                  onChange={(e) => set({ public_slug: e.currentTarget.value.toLowerCase() })}
                  maxLength={40}
                  description={t('bookings.settings.slugHint')}
                  error={err('bookings.settings.error.slug')}
                  disabled={ro}
                  autoComplete="off"
                  spellCheck={false}
                />
                {saved.public_url && (
                  <Stack gap="xs">
                    <Text role="inputLabel">{t('bookings.settings.publicUrl')}</Text>
                    <Group gap="sm" wrap="wrap">
                      <Text role="bodySmall" style={{ wordBreak: 'break-all' }}>{saved.public_url}</Text>
                      <CopyButton value={saved.public_url}>
                        {({ copied, failed, copy }) => (
                          <Button size="xs" variant="default" onClick={copy}>
                            {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                          </Button>
                        )}
                      </CopyButton>
                      {saved.public_page_enabled && (
                        <a href={saved.public_url} target="_blank" rel="noreferrer">{t('bookings.settings.openPage')}</a>
                      )}
                    </Group>
                    {!saved.public_page_enabled && <Text role="caption" c={MUTED}>{t('bookings.settings.publicOffHint')}</Text>}
                  </Stack>
                )}
              </Stack>
            </Card>
            )}

            {section === 'general' && (
            <Card withBorder data-guide="bookings-settings-general">
              <Stack gap="md">
                <Title order={4}>{t('bookings.settings.section.general')}</Title>
                <Textarea
                  label={t('bookings.settings.address')}
                  value={d.business_address}
                  onChange={(e) => set({ business_address: e.currentTarget.value })}
                  minRows={2}
                  maxLength={300}
                  description={t('bookings.settings.addressHint')}
                  disabled={ro}
                />
                <TextInput
                  label={t('bookings.settings.prefix')}
                  value={d.number_prefix}
                  onChange={(e) => set({ number_prefix: e.currentTarget.value.toUpperCase() })}
                  maxLength={8}
                  description={t('bookings.settings.prefixHint', { example: `${d.number_prefix.trim()}1042` })}
                  error={err('bookings.settings.error.prefix')}
                  disabled={ro}
                />
              </Stack>
            </Card>
            )}

            {section === 'general' && (
            <Card withBorder>
              <Stack gap="xs">
                <Title order={4}>{t('bookings.settings.limits')}</Title>
                <Text role="bodySmall">
                  {limits?.booking_staff == null ? t('bookings.settings.limitStaffNone') : t('bookings.settings.limitStaff', { n: limits.booking_staff })}
                </Text>
                <Text role="bodySmall">
                  {limits?.booking_services == null ? t('bookings.settings.limitServicesNone') : t('bookings.settings.limitServices', { n: limits.booking_services })}
                </Text>
                <Text role="caption" c={MUTED}>{t('bookings.settings.limitsHint')}</Text>
              </Stack>
            </Card>
            )}

            {canEdit && (
              <Group gap="sm">
                <Button type="submit" loading={save.isPending} disabled={!dirty}>{t('action.saveChanges')}</Button>
                {tried && problem && <Text role="caption" c="var(--rr-color-error)">{t('bookings.settings.fixFirst')}</Text>}
              </Group>
            )}
          </Stack>
        </form>
      )}
    </Stack>
  );
}
